import { createFeature, createReducer, on } from '@ngrx/store';
import { ID } from 'src/app/core/definitions/types';
import { LinkSummary } from 'src/app/core/interfaces/link-summary';
import { PatternSummary } from 'src/app/core/interfaces/pattern-summary.interface';
import { treeHasErrors } from '../../../core/helpers/pattern-utils';
import {
    adaptToState,
    ErrorType,
    TreeValidationState
} from '../../../core/interfaces/general-validation-result.interface';
import { PatternDetail } from '../../../core/interfaces/pattern-detail.interface';
import { PushSummary } from '../../../core/interfaces/push-summary.interface';
import { Push } from '../../../core/interfaces/push.interface';
import { PushByFramework } from '../../../core/interfaces/pushes-by-framework.interface';
import { discardUnsavedChangesAndNavigate } from '../../../store/actions/root.actions';
import { HeaderItem } from '../../layout/interfaces/header-item';
import * as patternsFiltersActions from './actions/pattern-filters.action';
import * as patternsApiActions from './actions/patterns-api.action';
import * as patternsActions from './actions/patterns.action';

export interface PatternsState {
    creationMode: boolean;
    patterns: PatternSummary[];
    draftPatterns: PatternDetail[];
    selectedPattern: PatternDetail | null;
    modified: boolean;
    patternHeader: HeaderItem | null;
    tryingPattern: boolean;
    loadingDetail: boolean;
    loadingPushes: boolean;
    selectedPush: Push | null;
    pushes: PushByFramework[] | null;
    drawingToMostRecentPush: Record<ID, PushSummary>;
    loadingPush: boolean;
    loadingPatternList: boolean;
    associatedLinks: LinkSummary[];
    loadingAssociatedLinks: boolean;
    archivedPatterns: PatternSummary[];
    loadingArchivedList: boolean;

    /**
     * The result of the pattern validation
     */
    patternValidationResult: TreeValidationState | null;

    /**
     * Errors (400 or 500) on the pattern validation
     */
    errorsOnValidation: string[];
}

export const initialState: PatternsState = {
    creationMode: false,
    draftPatterns: [],
    patterns: [],
    selectedPattern: null,
    modified: false,
    patternHeader: null,
    loadingDetail: false,
    loadingPushes: false,
    loadingPush: false,
    loadingPatternList: false,
    selectedPush: null,
    pushes: null,
    drawingToMostRecentPush: {},
    tryingPattern: false,
    patternValidationResult: null,
    associatedLinks: [],
    loadingAssociatedLinks: false,
    archivedPatterns: [],
    loadingArchivedList: false,
    errorsOnValidation: []
};

export const patternsFeature = createFeature({
    name: 'patterns',
    reducer: createReducer(
        initialState,
        on(patternsApiActions.getPatternById, (state) => ({
            ...state,
            creationMode: false,
            selectedPattern: null,
            modified: false,
            loadingDetail: true,
            tryingPattern: false,
            patternHeader: null
        })),
        on(patternsApiActions.getArchivedPatternById, (state) => ({
            ...state,
            creationMode: false,
            selectedPattern: null,
            modified: false,
            loadingDetail: true,
            tryingPattern: false,
            patternHeader: null
        })),
        on(patternsApiActions.getPatternByIdSuccess, (state, { pattern }) => ({
            ...state,
            selectedPattern: pattern,
            loadingDetail: false
        })),
        on(patternsApiActions.getPatternByIdFail, (state) => ({
            ...state,
            loadingDetail: false
        })),
        on(patternsActions.setPattern, (state, { pattern, isDraft }) => {
            return {
                ...state,
                creationMode: isDraft,
                selectedPattern: pattern,
                tryingPattern: false
            };
        }),
        on(patternsActions.cleanPatternHeader, (state) => {
            return {
                ...state,
                patternHeader: null
            };
        }),
        on(patternsApiActions.deletePattern, (state) => ({
            ...state,
            loadingDetail: true,
            patternHeader: null
        })),
        on(patternsApiActions.deletePatternSuccess, (state) => ({
            ...state,
            loadingDetail: false
        })),
        on(patternsApiActions.deletePatternFail, (state) => ({
            ...state,
            selectedPattern: null,
            loadingDetail: false
        })),
        on(patternsApiActions.getPatterns, (state) => ({
            ...state,
            patterns: [],
            tryingPattern: false,
            loadingPatternList: true
        })),
        on(patternsApiActions.getPatternsSuccess, (state, { patterns }) => ({
            ...state,
            patterns,
            loadingPatternList: false
        })),
        on(patternsApiActions.getPatternsFail, (state) => ({
            ...state,
            loadingPatternList: false
        })),
        on(patternsApiActions.getArchivedPatterns, (state) => ({
            ...state,
            archivedPatterns: [],
            loadingArchivedList: true
        })),
        on(
            patternsApiActions.getArchivedPatternsSuccess,
            (state, { archivedPatterns }) => ({
                ...state,
                archivedPatterns,
                loadingArchivedList: false
            })
        ),
        on(patternsApiActions.getArchivedPatternsFail, (state) => ({
            ...state,
            loadingArchivedList: false
        })),
        on(patternsApiActions.createPattern, (state) => ({
            ...state,
            loadingDetail: true
        })),
        on(patternsApiActions.createPatternSuccess, (state, { draftId, pattern }) => ({
            ...state,
            creationMode: false,
            draftPatterns: state.draftPatterns.filter((p) => p.id !== draftId),
            patterns: [pattern, ...state.patterns],
            selectedPattern: pattern,
            modified: false,
            loadingDetail: false
        })),
        on(patternsApiActions.createPatternFail, (state) => ({
            ...state,
            loadingDetail: false
        })),
        on(patternsActions.createPatternDraft, (state, { draft }) => ({
            ...state,
            selectedPush: null,
            selectedPattern: null,
            patternHeader: null,
            draftPatterns: [...state.draftPatterns, draft]
        })),
        on(patternsActions.deletePatternDraft, (state, { id }) => ({
            ...state,
            draftPatterns: state.draftPatterns.filter((p) => p.id !== id)
        })),
        on(patternsApiActions.deletePatternSuccess, (state, { id }) => ({
            ...state,
            patterns: state.patterns.filter((p) => p.id !== id),
            selectedPattern: null
        })),
        on(patternsApiActions.updatePattern, (state) => ({
            ...state,
            loadingDetail: true,
            selectedPattern: null
        })),
        on(patternsApiActions.updatePatternSuccess, (state, { pattern }) => ({
            ...state,
            patterns: state.patterns.map((p) => (p.id === pattern.id ? pattern : p)),
            selectedPattern: pattern,
            modified: false,
            loadingDetail: false
        })),
        on(patternsApiActions.updatePatternFail, (state, { initialPattern }) => ({
            ...state,
            loadingDetail: false,
            selectedPattern: initialPattern // make sure we don't lose the initial pattern after a failed update
        })),
        on(patternsActions.changeDetailHeader, (state, { patternChanged }) => {
            if (
                state.selectedPattern?.title === patternChanged.title &&
                state.selectedPattern.description === patternChanged.description
            ) {
                return state;
            }

            return {
                ...state,
                patternHeader: {
                    name: patternChanged.title,
                    description: patternChanged.description
                },
                modified: true
            };
        }),
        on(patternsActions.patternDetailDiscard, (state, { discard }) => {
            const drafts =
                discard && state.creationMode
                    ? state.draftPatterns.filter(
                          (item) => item.id !== state.selectedPattern?.id
                      )
                    : state.draftPatterns;

            return {
                ...state,
                modified: false,
                patternHeader: discard ? null : state.patternHeader,
                selectedPattern: discard ? null : state.selectedPattern,
                loadingDetail: discard ? true : state.loadingDetail,
                draftPatterns: drafts,
                selectedPush: discard ? null : state.selectedPush
            };
        }),
        on(patternsActions.setPatternModified, (state, { modified }) => {
            return {
                ...state,
                modified
            };
        }),
        on(patternsFiltersActions.editFilter, (state) => {
            return {
                ...state,
                modified: true
            };
        }),
        on(patternsFiltersActions.deleteFilter, (state) => {
            return {
                ...state,
                modified: true
            };
        }),
        on(patternsFiltersActions.isSourceChildChange, (state) => {
            return {
                ...state,
                modified: true
            };
        }),

        on(
            patternsActions.pushSelectionChange,
            (state, { id, autoVersion, targetFramework }) => {
                if (!state.selectedPattern || !id) return state;
                const newSelectedPattern = {
                    ...state.selectedPattern,
                    autoVersion,
                    selectedDrawing: autoVersion ? id : '',
                    selectedPush: autoVersion ? '' : id,
                    targetFramework: targetFramework
                };
                return {
                    ...state,
                    selectedPattern: newSelectedPattern,
                    modified: true
                };
            }
        ),
        on(patternsActions.setTryingPattern, (state, { tryingPattern }) => ({
            ...state,
            tryingPattern
        })),
        on(patternsActions.patternTreeChange, (state, { autocadTree, workRoadsTree }) => {
            const hasErrors = treeHasErrors(workRoadsTree);
            return {
                ...state,
                selectedPattern: {
                    ...state.selectedPattern,
                    workRoadsTree,
                    autocadTree,
                    hasErrors
                } as any
            };
        }),
        on(patternsActions.getPushes, (state) => ({
            ...state,
            pushes: null,
            loadingPushes: true
        })),
        on(patternsActions.getPushesSuccess, (state, { linkGroup }) => ({
            ...state,
            pushes: linkGroup.pushes,
            loadingPushes: false,
            drawingToMostRecentPush: linkGroup.drawingToMostRecentPush
        })),
        on(patternsActions.getPushesFail, (state) => ({
            ...state,
            pushes: null,
            loadingPushes: false
        })),
        on(patternsActions.getPush, (state) => ({
            ...state,
            selectedPush: null,
            loadingPush: true
        })),
        on(patternsActions.getPushSuccess, (state, { push }) => ({
            ...state,
            selectedPush: push,
            loadingPush: false
        })),
        on(patternsActions.navigateToPattern, (state) => {
            return {
                ...state,
                selectedPush: null,
                loadingPush: false
            };
        }),
        on(patternsActions.navigateToArchivedPattern, (state) => {
            return {
                ...state,
                selectedPush: null,
                loadingPush: false
            };
        }),
        on(patternsApiActions.validatePatternSuccess, (state, { result }) => ({
            ...state,
            patternValidationResult: adaptToState(result),
            errorsOnValidation: []
        })),
        on(patternsApiActions.validatePatternFail, (state, { errors }) => {
            return {
                ...state,
                patternValidationResult: {
                    nodeErrors: {},
                    globalErrors: {
                        errors: [],
                        warnings: []
                    },
                    validationErrors: errors.map((error) => ({
                        errorMessage: error,
                        errorType: ErrorType.UNKNOWN,
                        fieldId: '',
                        nodeId: ''
                    }))
                }
            };
        }),
        on(patternsApiActions.fieldChangeResetGeneralErrors, (state) => ({
            ...state,
            patternValidationResult: null,
            errorsOnValidation: []
        })),
        on(discardUnsavedChangesAndNavigate, (state) => ({
            ...state,
            draftPatterns: initialState.draftPatterns,
            modified: false
        })),
        on(patternsActions.getAssociatedLinks, (state) => {
            return {
                ...state,
                loadingAssociatedLinks: true
            };
        }),
        on(patternsApiActions.getAssociatedLinksSuccess, (state, { links }) => ({
            ...state,
            loadingAssociatedLinks: false,
            associatedLinks: links
        })),
        on(patternsActions.cleanLinkAssociations, (state) => ({
            ...state,
            loadingAssociatedLinks: false,
            associatedLinks: []
        }))
    )
});
